import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormEntryComponentClass, FormComponentLookup, FormBroadcast } from '@ptl/forms';
import { Subscription } from 'rxjs';
import { ContentStorageService } from '@ptl/global-components';
import { ZipCountyStateStructure } from 'src/app/angular-elements/shared/interfaces/zipCountyStateStructure.interface';
import { EffectiveDateRetrievalOptions, FamilyFormService, ConsumerEffectiveDate  } from '../../family-form/services/family-form.service';
import { DateService } from 'src/app/shared/services/date.service';
import { setEffectiveDate } from 'src/app/angular-elements/shared/components/configs/reducers/effective-date.reducer';
import { EffectiveDateStateStructure } from 'src/app/angular-elements/shared/components/configs/interfaces/effective-date.state-structure.interface';
import { plansNeedRefreshedFromBackend } from 'src/app/angular-elements/plan-selection/configs/reducers/plans.reducer';
import { GetOpenEnrollmentRule } from '../../family-form/types/qualifying-life-event.response.interface';
import { Moment } from 'moment';
import { SpecialEnrollmentLifeEventDrivenEffectiveDateService } from './special-enrollment-life-event-driven-effective-date.service';
import { SepLifeEventService } from 'src/app/shared/services/sep-life-event.service'

@FormComponentLookup('individual-market-effective-date-select')
@Component({
    selector: 'app-individual-market-effective-date-select',
    templateUrl: './individual-market-effective-date-select.component.html',
    styleUrls: ['./individual-market-effective-date-select.component.scss'],
    standalone: false
})
export class IndividualMarketEffectiveDateSelectComponent extends FormEntryComponentClass implements OnInit, OnDestroy {

  contentStorageSubscription: Subscription;
  contentStorageKey = 'SpecialEnrollmentPeriodContent';
  
  isSEP: boolean = true;
  effectiveDate: EffectiveDateStateStructure;
  region: string;
  fetchFromConfig: boolean;
  // spinner
  loadingOptions = false;
  qualifyingLifeEvents: any;

  constructor(
    private familyFormService: FamilyFormService,
    private contentStorageService: ContentStorageService,
    private date: DateService,
    private sepEffectiveDateService: SpecialEnrollmentLifeEventDrivenEffectiveDateService,
    private sepLifeEventService: SepLifeEventService
  ) {
    super();
  }

  ngOnInit() {
    this.contentStorageSubscription = this.contentStorageService.contentChanged$.subscribe(async () => {
      this.callConsumerService();
    });

    this.effectiveDateValueChanges.subscribe( (effectiveDateControlValue: string | null) => {
      if (effectiveDateControlValue) {
        const dateAsMoment = this.date.getDateAsMoment(effectiveDateControlValue);

        // Manually trigger forms event - to allow forms to react
        this.triggerFormStreamEffectiveDateYear(dateAsMoment);
        const effectiveDate: string = dateAsMoment.format('MM/DD/YYYY');

        // Manually trigger dispatch - to get cookie assigned
        this.ngRedux.dispatch(setEffectiveDate(effectiveDate));
      }
    });
    this.fetchFromConfig = this.reducerUtilityService.getComponentStates().SepLifeEventForm.fetchFromConfig
  }

  get effectiveDateValueChanges() {
    return this.formControls[this.entry.formControlAttributes.formControlName].valueChanges;
  }
  
  private toggleSpinner() {
    this.loadingOptions = !this.loadingOptions;
  }

  async callConsumerService() {
    const content = this.contentStorageService.getContentMap(this.contentStorageKey);
    if (content?.size > 0) {
      if (content.get('isSEP') && content.get('isSEP') === 'false') {
        this.isSEP = false;
      } else {
        this.isSEP = true;
      }

      this.clearEffectiveDatesDropdown();

      this.toggleSpinner();
      await this.setConsumerEffectiveDateOptions(this.isSEP);
      this.toggleSpinner();

    }
  }

  /** Clear dropdown */
  ngOnDestroy() {
    this.clearEffectiveDatesDropdown();
    this.sepLifeEventService.clearSepFormData();
  }

  async setConsumerEffectiveDateOptions(isSEP: boolean) {
    
    const zipCountyState: ZipCountyStateStructure = this.reducerUtilityService.getComponentStates()['ZipCounty'];
    const payloadStates = this.reducerUtilityService.getPayloadStates();
    const sepLifeEventObj = this.sepLifeEventService.getSepLifeEventFromReduxData();
    if (zipCountyState) {
      this.region = zipCountyState.county.region

      const options: EffectiveDateRetrievalOptions = {
        isSep: isSEP,
        region: zipCountyState.county.region,
        offExchangeIndicator: 'true',
        isDental: this.entry?.custom?.isDental,
        isBeb: this.entry?.custom?.isBeb,
        sepLifeEvent: sepLifeEventObj ?? undefined
      }
      
      const effectiveDates: ConsumerEffectiveDate = await this.familyFormService.getEffectiveDates(options).catch((err) => {
        console.warn('ConsumerEffectiveDate failed.', err);
        return {
          payload: {
            consumerEffectiveDateSingle: (this.entry?.custom.useOverride && this.entry?.custom?.consumerEffectiveDateSingleOverride) ?? [],
            calculationMetadata: {}
          }
        } as ConsumerEffectiveDate;
      });


      const serviceEffectiveDates = (effectiveDates?.payload?.consumerEffectiveDateSingle).map(obj => this.date.getDateAsMoment(obj.displayEffDate));

      if (this.isSEP) {
        let isApplicableCircumstance;
        let lifeEvent: GetOpenEnrollmentRule;


        isApplicableCircumstance = payloadStates?.['special-enrollment-circumstance']?.isApplicableCircumstance;
        lifeEvent = this.fetchFromConfig ? payloadStates?.['special-enrollment-circumstance']?.lifeEvent : 
                                this.reducerUtilityService.getComponentStates().SepLifeEventForm?.selectedLifeEvent;

        /** SPECIAL ENROLLMENT */
        if (isApplicableCircumstance === 'Yes') {
        let customSepEffectiveDates: Moment[] = [];;
          if(!(effectiveDates?.payload?.calculationMetadata?.sepEffectiveDateCalculated))
            customSepEffectiveDates = this.sepEffectiveDateService.getSepEffectiveDates(lifeEvent, serviceEffectiveDates);
          
          if (customSepEffectiveDates?.length > 0) {
            // Format and add each effective date to display
            customSepEffectiveDates.forEach( (effDate: Moment) => {
              /** Push qualifying life event effective date */
              this.entry.selectAttributes.optionAttributes.push({
                label: effDate.format('LL'),
                value: effDate.toISOString()
              });
            });

          } else {
            if (serviceEffectiveDates && serviceEffectiveDates.length > 0) {
              for (const effDate of serviceEffectiveDates) {
                this.entry.selectAttributes.optionAttributes.push({
                  label: effDate.format('LL'),
                  value: effDate.toISOString()
                });
              }
            }
          }
        }
      }
      /** OPEN ENROLLMENT */
      else {

        if (effectiveDates?.payload) {
          if (serviceEffectiveDates && serviceEffectiveDates.length > 0) {
            for (const effDate of serviceEffectiveDates) {
              this.entry.selectAttributes.optionAttributes.push({
                label: effDate.format('LL'),
                value: effDate.toISOString()
              });
            }
          }

        }
      }

      /** Filter out duplicate options && sort after adding new effective dates*/
      this.entry.selectAttributes.optionAttributes = this.sortAndFilterEffectiveDates(this.entry?.selectAttributes?.optionAttributes);
      
      /** Pre-select value */
      this.entry.selectAttributes.optionAttributes.forEach(option => {
        if (option.selected) {
          const dynamicObj = {};
          dynamicObj[this.entry.formControlAttributes.formControlName] = option.value;
          this.form.patchValue(dynamicObj);
        }
      });
    }
  }

  /** Programmatically trigger an event to the formStream */
  private triggerFormStreamEffectiveDateYear(date: Moment) {
    const evt: FormBroadcast = {
      formControlName: 'SXEANG_PROXY_EFFECTIVE_DATE_PLAN_YEAR_TRIGGER',
      type: 'valueChange',
      value: date.get('year').toString()
    };
    this.formStreamTrigger.triggerFormStream(evt);
  }

  onEffectiveDateChange(event: string) {
    const date = this.date.getDateAsMoment(event);
    const prettyDate = date.format("YYYY-MM-DD");

    setTimeout(() => {
      this.ngRedux.dispatch(setEffectiveDate(prettyDate));
      this.date.setDirtyEffectiveDate();
      this.ngRedux.dispatch(plansNeedRefreshedFromBackend(true));
    }, 1);

  }

  clearEffectiveDatesDropdown() {
    this.entry.selectAttributes.optionAttributes = [
      {
        label: 'Select an Effective Date',
        value: ''
      }
    ];
  }

  sortAndFilterEffectiveDates(effectiveDateList){
      effectiveDateList = effectiveDateList.sort((a, b) => {
        const aDate = this.date.getDateAsMoment(a.value);
        const bDate = this.date.getDateAsMoment(b.value);

        if (aDate.isBefore(bDate)) {
          return -1;
        } else if (aDate.isAfter(bDate)) {
          return 1
        } else {
          return 0
        }
      }).filter((item, index, objects) => {
        if (index === 0) {
          return item;
        } else if (item.label !== objects[index - 1].label) {
          return item;
        }
      });

    return effectiveDateList;
  }
}
